import { apiPostFormData } from './apiUtils';

export interface DocumentUploadRequest {
  file: File;
  specialty?: string;
}

export interface DocumentProtocolResponse {
  success: boolean;
  data: {
    content: string; // Заменили json_protocol на content
    confidence: number;
    processing_time: number;
  };
  warnings: string[];
  errors: string[];
}

// Генерация протокола из документа (PDF, DOCX, XLSX)
export const generateProtocolFromDocument = async (request: DocumentUploadRequest): Promise<DocumentProtocolResponse> => {
  const formData = new FormData();
  formData.append('file', request.file);
  
  if (request.specialty) {
    formData.append('specialty', request.specialty);
  }

  // Используем новый эндпоинт для генерации протокола из документа
  const response = await apiPostFormData('/protocols/generate-from-document', formData);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to generate protocol from document: ${response.status} - ${errorText}`);
  }

  return response.json();
};

// Валидация документа (PDF, DOCX, XLSX)
export const validateDocumentFile = (file: File): { isValid: boolean; error?: string } => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  
  // Поддерживаемые форматы
  const supportedExtensions = ['.pdf', '.docx', '.xlsx'];
  const supportedMimeTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];

  // Проверяем расширение файла
  const hasValidExtension = supportedExtensions.some(ext => fileName.endsWith(ext));
  // Проверяем MIME тип
  const hasValidMimeType = supportedMimeTypes.includes(fileType) || fileType === '';

  if (!hasValidExtension && !hasValidMimeType) {
    return { 
      isValid: false, 
      error: 'Поддерживаются только файлы форматов: PDF, Word (.docx), Excel (.xlsx)' 
    };
  }

  // Проверяем размер файла (максимум 20MB для офисных документов)
  const maxSize = 20 * 1024 * 1024; // 20MB
  if (file.size > maxSize) {
    return { isValid: false, error: 'Размер файла не должен превышать 20MB' };
  }

  return { isValid: true };
};

// Получение типа документа по файлу
export const getDocumentType = (file: File): 'pdf' | 'docx' | 'xlsx' | 'unknown' => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  if (fileName.endsWith('.pdf') || fileType.includes('pdf')) {
    return 'pdf';
  }
  if (fileName.endsWith('.docx') || fileType.includes('wordprocessingml')) {
    return 'docx';
  }
  if (fileName.endsWith('.xlsx') || fileType.includes('spreadsheetml')) {
    return 'xlsx';
  }
  
  return 'unknown';
};

// Получение иконки для типа документа
export const getDocumentIcon = (file: File): string => {
  const type = getDocumentType(file);
  switch (type) {
    case 'pdf': return '📄';
    case 'docx': return '📝';
    case 'xlsx': return '📊';
    default: return '📄';
  }
};



// Форматирование размера файла
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
